/**
 * @file delivery-block-state.ts
 * @description Реестр состояний блока «Доставка» в карточке заказа
 * (phase19.24 шаг 5.2, проект `phase19.24-step5-2-delivery-block-design.md` §4).
 *
 * Одно место решает три вопроса сразу: какой бейдж, что написано менеджеру и
 * как подписана главная кнопка. До реестра те же полтора десятка ситуаций
 * разбирались условиями в трёх компонентах, и «Подтверждено» в шапке уживалось
 * с формой «Отправить на подтверждение» внизу.
 *
 * Новое состояние = запись в `DELIVERY_BLOCK_STATE_META` плюс проверка в
 * `resolveDeliveryBlockState`, а не ветка в разметке (правило OCP,
 * docs/processes/engineering-principles.md).
 *
 * Порядок проверок = порядок запретов: сначала то, что вообще закрывает
 * правку (нет прав, заказ закрыт, деньги получены — F26, стадия заказа), и
 * только потом состояние заявки и полнота условий. Иначе экран предлагал бы
 * «отправить повторно» по оплаченному заказу, а сервер отвечал
 * `receipt_locked_paid`.
 *
 * Инвариант: кнопки не бывает там, где сервер откажет. Запрет от сервера
 * (`lockReason`) закрывает и форму, и главную кнопку; единственное действие
 * при стадийном запрете — путь, который сервер сам и назвал (`nextStep`).
 */

import type {
  DeliveryAgreementReceiptDto,
  DeliveryAgreementStateView,
  DeliveryLockReason,
  OrderDeliveryNextStep,
  OrderReceiptSource,
} from "./api-delivery-agreement";
import { ORDER_STAGE_KIND_LABEL, type OrderStageKind } from "./api-orders";
import { isSelectableReceiptMethod, RECEIPT_METHOD_META } from "./receipt-method";

/** Состояние блока «Доставка». */
export type DeliveryBlockState =
  | "NO_PERMISSION"
  | "CLOSED"
  | "LOCKED_PAID"
  | "STAGE_LOCKED"
  | "STAGE_NEEDS_AGREEMENT"
  | "OUTDATED"
  | "SENT"
  | "OVERDUE"
  | "CANCELLED"
  | "CONFIRMED"
  | "SITE_CHOICE"
  | "CHANGING"
  | "METHOD_MISSING"
  | "ADDRESS_MISSING"
  | "QUOTE_FAILED"
  | "CARRIER_NOT_CONFIGURED"
  | "READY";

/** Вариант бейджа shadcn — те же тона, что у статусов заявки. */
export type DeliveryBlockTone =
  | "warning"
  | "outline"
  | "success"
  | "destructive"
  | "default";

/** Общая часть записи реестра: то, что менеджер читает и видит. */
interface DeliveryBlockStateBase {
  /** Бейдж рядом с заголовком; `null` — бейджа нет (роль без прав). */
  badge: string | null;
  tone: DeliveryBlockTone;
  /** Что происходит и что делать — одной фразой, без «внимание». */
  text: string;
  /** Показать кнопку «Изменить условия доставки» (правка через подтверждение). */
  change: boolean;
  /** Точка на вкладке «Доставка»: ход наш, заказ стоит из-за нас. */
  dot: boolean;
  /**
   * Запись уточняется стадией заказа: бейдж берётся из `STAGE_LOCK_BADGE`
   * («Заказ в сборке» вместо общего «Условия закрыты»), а текст — из
   * `STAGE_LOCK_TEXT`. Менеджеру важно, ЧТО именно мешает правке.
   */
  byStage?: boolean;
  /**
   * Кнопка «Согласовать доставку» — перевод стадии, а не письмо клиенту.
   * Единственное состояние с ней — `STAGE_NEEDS_AGREEMENT`: форму сервер
   * отвергнет, пока заказ на «Заявке».
   */
  agree?: boolean;
  /**
   * Печатать ли в сводке ссылку подтверждения. `false` — у роли нет права
   * вести заказы: диктовать ссылку ей нечего (сервер отдаёт `confirmUrl: null`,
   * но сводка не должна зависеть от того, добрался ли до неё этот null).
   */
  confirmLink?: boolean;
}

/**
 * Запись реестра: всё, что блок знает о состоянии.
 *
 * Тип — разделённое объединение по `form`: подпись главной кнопки бывает
 * ТОЛЬКО у состояния с открытой формой. До этого `primaryLabel` стоял и у
 * состояний-читалок («Подтверждено», «Ждём клиента») и обманывал: кнопки с
 * такой подписью на экране не было (находка ревью 5.2).
 */
export type DeliveryBlockStateMeta =
  | (DeliveryBlockStateBase & {
      /** Форма условий открыта сразу (ход наш и клиент ничего не ждёт). */
      form: true;
      /**
       * Подпись главной кнопки. «Повторное согласование» — там, где клиент
       * уже что-то видел: правка обязана уйти ему новым письмом (F30, решение
       * пользователя 2026-09-21).
       */
      primaryLabel: string;
    })
  | (DeliveryBlockStateBase & { form: false; primaryLabel?: never });

/** Подпись главной кнопки для первой отправки. */
const SEND = "Отправить на подтверждение";
/** Подпись, когда клиент уже видел условия: правка = новое письмо. */
const RESEND = "Отправить на повторное согласование";

export const DELIVERY_BLOCK_STATE_META: Record<
  DeliveryBlockState,
  DeliveryBlockStateMeta
> = {
  NO_PERMISSION: {
    badge: null,
    tone: "default",
    text: "Условия доставки ведёт менеджер заказов — здесь только сводка.",
    form: false,
    change: false,
    dot: false,
    // Ссылку подтверждения диктует клиенту менеджер заказов: роли без права
    // вести заказы она не нужна и не показывается.
    confirmLink: false,
  },
  CLOSED: {
    badge: "Заказ закрыт",
    tone: "default",
    text: "Заказ завершён или отменён — условия доставки остаются историей.",
    form: false,
    change: false,
    dot: false,
  },
  LOCKED_PAID: {
    badge: "Заморожено после оплаты",
    tone: "outline",
    text:
      "По заказу получены деньги: способ, адрес и стоимость заморожены (F26). " +
      "Разницу проводите доплатой или возвратом, другие условия — новым заказом.",
    form: false,
    change: false,
    dot: false,
  },
  STAGE_LOCKED: {
    // Бейдж — название стадии: «Заказ в сборке» объясняет запрет сам, а общее
    // «Условия закрыты» заставляло менеджера искать причину. Текст по умолчанию
    // нейтральный: «заказ уже собирают» верно только от сборки и дальше.
    badge: "Условия закрыты",
    tone: "outline",
    text: "На этой стадии условия доставки не правятся.",
    form: false,
    change: false,
    // Ход не наш и не клиента — заказ занят другой работой, звать сюда нечем.
    dot: false,
    byStage: true,
  },
  STAGE_NEEDS_AGREEMENT: {
    badge: "Заявка",
    tone: "outline",
    text:
      "Чтобы изменить условия, переведите заказ в согласование доставки — " +
      "на «Заявке» сервер правку условий не примет.",
    form: false,
    change: false,
    dot: false,
    byStage: true,
    agree: true,
  },
  OUTDATED: {
    badge: "Устарело",
    tone: "destructive",
    text:
      "Заказ изменили после отправки — предложение недействительно, отправьте заново.",
    primaryLabel: RESEND,
    form: true,
    change: false,
    dot: true,
  },
  SENT: {
    badge: "Ждём клиента",
    tone: "outline",
    text:
      "Предложение у клиента. Форма закрыта, чтобы вы не отправили одно, " +
      "а клиент подтвердил другое.",
    form: false,
    change: false,
    dot: false,
  },
  OVERDUE: {
    badge: "Ждём клиента · просрочено",
    tone: "destructive",
    text:
      "Клиент не ответил в срок. Позвоните и отправьте заново — старая ссылка не работает.",
    form: false,
    change: false,
    dot: true,
  },
  CANCELLED: {
    badge: "Заявка отменена",
    tone: "outline",
    text: "Выставите условия заново — заказ остаётся на согласовании.",
    primaryLabel: SEND,
    form: true,
    change: false,
    dot: true,
  },
  CONFIRMED: {
    // Без «✓» (SC43, phase19.31): статус словом, цвет даёт тон.
    badge: "Подтверждено",
    tone: "success",
    text:
      "Клиент согласился с условиями. Менять ещё можно, но это повторное согласование.",
    form: false,
    change: true,
    dot: false,
  },
  SITE_CHOICE: {
    badge: "Выбрано клиентом на сайте",
    tone: "outline",
    text:
      "Клиент выбрал условия при оформлении. Менять их можно, пока по заказу нет оплаты: " +
      "любое изменение способа, адреса, ПВЗ или суммы уходит клиенту новым письмом (F30).",
    form: false,
    change: true,
    dot: false,
  },
  CHANGING: {
    // Правка того, что клиент уже видел: подпись кнопки обязана отличаться от
    // первой отправки, иначе менеджер не понимает, что уйдёт новое письмо
    // (решение пользователя 2026-09-21 — любое изменение согласуем заново).
    badge: "Повторное согласование",
    tone: "warning",
    text:
      "Клиент уже видел эти условия. После правки ему уйдёт новое письмо, " +
      "прежняя ссылка перестанет работать, и заказ будет ждать ответа.",
    primaryLabel: RESEND,
    form: true,
    change: false,
    dot: true,
  },
  METHOD_MISSING: {
    badge: "Способ не указан",
    tone: "warning",
    text:
      "Выберите, как клиент получит заказ — от этого зависят сумма, порядок оплаты и маршрут.",
    primaryLabel: SEND,
    form: true,
    change: false,
    dot: true,
  },
  ADDRESS_MISSING: {
    badge: "Адрес не указан",
    tone: "warning",
    text:
      "Заполните адрес или выберите ПВЗ — без них заказ некуда отправить и тариф не посчитать.",
    primaryLabel: SEND,
    form: true,
    change: false,
    dot: true,
  },
  QUOTE_FAILED: {
    badge: "Расчёт не выполнен",
    tone: "warning",
    // Путь не блокируется: согласовать можно и с ручной суммой (§4).
    text: "Поставьте стоимость вручную и согласуйте — точный тариф пересчитаете позже.",
    primaryLabel: SEND,
    form: true,
    change: false,
    dot: true,
  },
  CARRIER_NOT_CONFIGURED: {
    badge: "Расчёт не выполнен",
    tone: "warning",
    text:
      "Перевозчик не настроен для расчёта. Стоимость можно поставить вручную — " +
      "что именно чинить, написано в окне расчёта.",
    primaryLabel: SEND,
    form: true,
    change: false,
    dot: true,
  },
  READY: {
    badge: "Не отправлено",
    tone: "warning",
    text: "Условия готовы: проверьте сумму и отправьте клиенту на подтверждение.",
    primaryLabel: SEND,
    form: true,
    change: false,
    dot: true,
  },
};

/** Что знает страница о доставке заказа на момент отрисовки блока. */
export interface DeliveryBlockStateInput {
  /**
   * Заявка F30; `null` — её ещё не завели. Тип — общий минимум
   * (`DeliveryAgreementStateView`): реестр обязан работать и с полной заявкой,
   * и с любым её сокращённым видом.
   */
  agreement: DeliveryAgreementStateView | null;
  /** Снапшот способа получения (из заявки или из `GET …/delivery`). */
  receipt?: DeliveryAgreementReceiptDto | null;
  /** Кто выставил условия: витрина или менеджер. */
  source?: OrderReceiptSource | null;
  /** Право менять условия; `false` без причины читается как «нет прав». */
  canEdit: boolean;
  lockReason?: DeliveryLockReason | null;
  /**
   * Единственный путь дальше при стадийном запрете (`lockReason: "stage"`):
   * `open_agreement` — сначала перевести заказ в согласование доставки.
   */
  nextStep?: OrderDeliveryNextStep | null;
  /** Стадия заказа — из неё берётся бейдж стадийного запрета. */
  stageKind?: OrderStageKind | null;
  /** Менеджер открыл форму руками («Изменить условия», «Изменить исход»). */
  editing?: boolean;
  /**
   * Код причины неудачного расчёта (`QUOTE_REASON_META`). Путь он не
   * блокирует — только объясняет, почему в поле стоимости пусто.
   */
  quoteReason?: string | null;
}

/** Причины блокировки — реестр вместо каскада условий (новая причина = строка). */
const LOCK_STATE: Record<DeliveryLockReason, DeliveryBlockState> = {
  no_permission: "NO_PERMISSION",
  closed: "CLOSED",
  paid: "LOCKED_PAID",
  partially_paid: "LOCKED_PAID",
  stage: "STAGE_LOCKED",
};

/** Путь дальше при стадийном запрете — тоже реестром (новый путь = строка). */
const NEXT_STEP_STATE: Record<OrderDeliveryNextStep, DeliveryBlockState> = {
  open_agreement: "STAGE_NEEDS_AGREEMENT",
};

/**
 * Бейдж стадийного запрета: язык заказа, а не воронки.
 *
 * Не `ORDER_STAGE_KIND_LABEL`: там подписи стадий («На сборке»), а рядом с
 * заголовком «Доставка» менеджер читает предложение о заказе («Заказ в
 * сборке»). Стадии нет в реестре — берём подпись воронки, а её нет — бейдж
 * записи состояния.
 */
const STAGE_LOCK_BADGE: Partial<Record<OrderStageKind, string>> = {
  CREATED: "Заявка",
  PACKAGING: "Заказ в сборке",
  PACKED: "Заказ собран",
  HANDED_TO_CARRIER: "Заказ отгружен",
  READY_FOR_PICKUP: "Заказ готов к выдаче",
  IN_TRANSIT: "Заказ в пути",
  ARRIVED_AT_PICKUP: "Заказ в пункте выдачи",
  DELIVERED: "Заказ доставлен",
  RETURN: "Возврат заказа",
};

/**
 * Бейдж блока: обычно из реестра состояний, при стадийном запрете — по стадии.
 *
 * Один вызов вместо `meta.badge` в разметке: иначе каждое место со своим
 * бейджем начинало бы решать про стадию само (было с `KIND_OPTIONS`).
 */
export function deliveryBlockBadge(
  state: DeliveryBlockState,
  stageKind?: OrderStageKind | null,
): string | null {
  const meta = DELIVERY_BLOCK_STATE_META[state];
  if (!meta.byStage || !stageKind) return meta.badge;
  return STAGE_LOCK_BADGE[stageKind] ?? ORDER_STAGE_KIND_LABEL[stageKind] ?? meta.badge;
}

/**
 * Стадии, где заказом уже занимается склад: только про них честно сказать
 * «заказ собирают или отгружают». На «Ожидании оплаты» фраза врала бы, поэтому
 * там остаётся нейтральный текст записи (дефект QA 5.2).
 */
const STAGE_IN_WORK =
  "Условия доставки не правятся на этой стадии: заказ уже собирают или " +
  "отгружают. Нужны другие условия — решайте их вместе с отгрузкой.";

const STAGE_LOCK_TEXT: Partial<Record<OrderStageKind, string>> = {
  PACKAGING: STAGE_IN_WORK,
  PACKED: STAGE_IN_WORK,
  HANDED_TO_CARRIER: STAGE_IN_WORK,
  READY_FOR_PICKUP: STAGE_IN_WORK,
  IN_TRANSIT: STAGE_IN_WORK,
  ARRIVED_AT_PICKUP: STAGE_IN_WORK,
  DELIVERED: STAGE_IN_WORK,
  RETURN: STAGE_IN_WORK,
};

/** Объясняющая строка блока: у стадийного запрета зависит от стадии. */
export function deliveryBlockText(
  state: DeliveryBlockState,
  stageKind?: OrderStageKind | null,
): string {
  const meta = DELIVERY_BLOCK_STATE_META[state];
  if (!meta.byStage || !stageKind) return meta.text;
  return STAGE_LOCK_TEXT[stageKind] ?? meta.text;
}

/** Причины отказа расчёта, которые чинятся в настройках, а не повтором. */
const SETUP_QUOTE_REASONS = new Set([
  "carrier_not_configured",
  "integration_not_connected",
  "sender_location_missing",
]);

/**
 * Клиент уже видел эти условия: выбрал их сам на витрине либо получил письмо.
 * Отправлять «первое» предложение такому заказу нельзя — только повторное.
 */
function clientSawConditions(input: DeliveryBlockStateInput): boolean {
  return (
    input.source === "SITE" ||
    !!input.agreement?.calculatedAt ||
    !!input.agreement?.confirmedAt
  );
}

/** Срок подтверждения вышел: ссылка мертва, ждать больше нечего. */
export function isAgreementOverdue(
  agreement: DeliveryAgreementStateView | null,
): boolean {
  if (!agreement?.expiresAt) return false;
  const until = new Date(agreement.expiresAt).getTime();
  return Number.isFinite(until) && until < Date.now();
}

/** Состояние блока по данным заказа — единственный источник для всех веток. */
export function resolveDeliveryBlockState(
  input: DeliveryBlockStateInput,
): DeliveryBlockState {
  const { agreement, receipt, editing } = input;

  if (input.lockReason) {
    const locked = LOCK_STATE[input.lockReason];
    // `nextStep` уточняет только стадийный запрет: «Заявка» ручного заказа —
    // не тупик, путь идёт через перевод в согласование. Для оплаты и закрытого
    // заказа пути нет, и подставлять кнопку туда нельзя.
    return locked === "STAGE_LOCKED" && input.nextStep
      ? NEXT_STEP_STATE[input.nextStep]
      : locked;
  }
  if (!input.canEdit) return "NO_PERMISSION";

  const status = agreement?.status ?? null;
  if (status === "OUTDATED") return "OUTDATED";
  if (status === "AWAITING_CONFIRMATION" && !editing) {
    return isAgreementOverdue(agreement) ? "OVERDUE" : "SENT";
  }
  if (status === "CANCELLED") return "CANCELLED";
  if (status === "CONFIRMED" && !editing) return "CONFIRMED";
  // Выбор клиента на витрине показываем только на чтение: менеджер сначала
  // говорит «меняю условия», и лишь потом правит (§3, заказ с сайта).
  if (input.source === "SITE" && receipt && !editing) return "SITE_CHOICE";

  if (!receipt || !isSelectableReceiptMethod(receipt.method)) return "METHOD_MISSING";
  const requires = RECEIPT_METHOD_META[receipt.method].requires;
  if (requires.carrier && !receipt.carrier) return "METHOD_MISSING";
  if (requires.address && !receipt.address?.trim()) return "ADDRESS_MISSING";
  // Правим то, что клиент уже видел (витрина или отправленное письмо) — это
  // повторное согласование, и кнопка обязана называться иначе. Подпись кнопки
  // важнее бейджа «Расчёт не выполнен»: «Отправить на подтверждение» по
  // заказу, который клиент уже видел, обещает не то, что произойдёт.
  if (editing && clientSawConditions(input)) return "CHANGING";
  if (input.quoteReason) {
    return SETUP_QUOTE_REASONS.has(input.quoteReason)
      ? "CARRIER_NOT_CONFIGURED"
      : "QUOTE_FAILED";
  }
  return "READY";
}
